import { createContext, useContext, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';

import { getApiSixClientId } from '~helpers';
import { CacheVersion } from '~types/appsync.types';

interface CacheVersionContextValue {
  cacheVersion?: number;
}

const CacheVersionContext = createContext<CacheVersionContextValue>({});

const GET_CACHE_VERSION = gql`
  query GetCacheVersion($clientId: String!) {
    getCacheVersion(clientId: $clientId) {
      clientId
      version
    }
  }
`;

type CacheVersionProviderProps = { readonly children?: React.ReactNode };

function CacheVersionProvider({ children }: CacheVersionProviderProps) {
  const clientId = getApiSixClientId();

  const { data, loading } = useQuery<{ getCacheVersion: CacheVersion }>(GET_CACHE_VERSION, {
    variables: { clientId },
  });

  const value = useMemo<CacheVersionContextValue>(
    () => ({ cacheVersion: data?.getCacheVersion?.version }),
    [data?.getCacheVersion?.version],
  );

  if (loading) return null;

  return <CacheVersionContext.Provider value={value}>{children}</CacheVersionContext.Provider>;
}

function useCacheVersion(): number | undefined {
  const { cacheVersion } = useContext(CacheVersionContext);
  return cacheVersion;
}

export { CacheVersionProvider, useCacheVersion };
